/* Sprint A — Business Plan Gold Panel.
 *
 * Read-time composer view. Renders 4 new cards:
 *   - Competitor Comparison (joined across competitor_analysis + brand_dna + research)
 *   - Aggregated SWOT
 *   - Influencer Block (from library match + social_strategy_reports)
 *   - Evidence-Grounded Facts (APPROVED only — pending/rejected/revoked excluded)
 *
 * Honest about sources via `source_summary` chip rail.
 */
import { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
    Sparkles, RefreshCw, Download, Shield, ShieldOff, TrendingUp,
    Megaphone, AlertCircle, ExternalLink,
} from "lucide-react";

const TIER_TONE = {
    tier_1: "border-violet-500/30 text-violet-300 bg-violet-500/5",
    tier_2: "border-sky-500/30 text-sky-300 bg-sky-500/5",
    tier_3: "border-zinc-700 text-zinc-400 bg-zinc-900/40",
};

function asArray(v) { return Array.isArray(v) ? v : []; }

export default function BusinessPlanGoldPanel({ projectId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/research-intel/projects/${projectId}/business-plan-gold`);
            setData(data);
        } catch (e) {
            toast.error(e?.userMessage || e?.response?.data?.detail || "Failed to load Gold view");
        } finally { setLoading(false); }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    const downloadPdf = () => {
        // Open in new tab — uses the existing get_user_header_or_query
        // auth pattern of the other /exports routes.
        const token = localStorage.getItem("token");
        const base = API || "";
        const url = `${base}/api/exports/${projectId}/business-plan-gold.pdf` + (token ? `?token=${encodeURIComponent(token)}` : "");
        window.open(url, "_blank", "noopener,noreferrer");
    };

    if (!data) {
        return (
            <div className="mt-4 card-base p-5" data-testid="business-plan-gold-panel-loading">
                <div className="text-xs text-zinc-500 inline-flex items-center gap-1">
                    <RefreshCw className={"w-3 h-3 " + (loading ? "animate-spin" : "")} /> Loading Gold view…
                </div>
            </div>
        );
    }

    const gold = data?.gold || {};
    const src = data?.source_summary || {};
    const comp = asArray(gold.competitor_comparison);
    const swot = gold.aggregated_swot || {};
    const inf = gold.influencer_block || {};
    const facts = gold.evidence_grounded_facts || {};
    const dist = asArray(gold.distribution_channels);

    return (
        <div className="mt-6 border border-zinc-800 rounded-sm bg-zinc-950/40"
             data-testid="business-plan-gold-panel">
            <div className="flex items-center justify-between gap-3 p-3 border-b border-zinc-800">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-400/80" />
                    <div>
                        <div className="text-xs font-mono uppercase tracking-wider text-amber-200">
                            Business Plan — Gold Layer
                        </div>
                        <div className="text-[10px] text-zinc-500 mt-0.5">
                            Read-time composition. No LLM. Joins existing Competitor + Research + Brand DNA + Social Strategy + Personas + Approved Evidence.
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={load} disabled={loading}
                            data-testid="bpg-refresh-btn"
                            className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-zinc-700 text-zinc-400 hover:text-zinc-200 rounded-sm disabled:opacity-40 inline-flex items-center gap-1">
                        <RefreshCw className={"w-3 h-3 " + (loading ? "animate-spin" : "")} />
                        Refresh
                    </button>
                    <button type="button" onClick={downloadPdf}
                            data-testid="bpg-download-pdf-btn"
                            className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 rounded-sm inline-flex items-center gap-1">
                        <Download className="w-3 h-3" /> PDF
                    </button>
                </div>
            </div>

            {/* Source-summary chip rail */}
            <div className="px-3 py-2 flex flex-wrap gap-1.5 border-b border-zinc-800" data-testid="bpg-source-summary">
                <SrcChip on={src.has_competitor_analysis} label="Competitor" />
                <SrcChip on={src.has_brand_dna} label="Brand DNA" />
                <SrcChip on={src.has_research} label="Research" />
                <SrcChip on={src.has_social_strategy_report} label="Social Strategy" />
                <SrcChip on={src.has_distribution_report} label="Distribution" />
                <SrcChip on={(src.personas_count || 0) > 0} label={`Personas: ${src.personas_count || 0}`} />
                <SrcChip on={(src.approved_evidence_count || 0) > 0} label={`Approved Evidence: ${src.approved_evidence_count || 0}`} />
            </div>

            {/* Body */}
            <div className="p-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Competitor Comparison */}
                <Card icon={TrendingUp} title="Competitor Comparison" testid="bpg-competitor-comparison">
                    {comp.length === 0 ? (
                        <Empty label="No competitor comparison data yet." />
                    ) : (
                        <div className="space-y-2">
                            {comp.map((c, i) => (
                                <div key={i} className="border border-zinc-800 rounded-sm p-2.5"
                                     data-testid={`bpg-competitor-row-${i}`}>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="text-sm font-medium text-zinc-100">{c.name}</div>
                                        <span className={"text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border rounded-sm " + (TIER_TONE[c.tier] || TIER_TONE.tier_3)}>
                                            {c.tier}
                                        </span>
                                    </div>
                                    {c.positioning ? (
                                        <div className="text-[11px] text-zinc-400 mt-1">{c.positioning}</div>
                                    ) : null}
                                    {c.research_positioning ? (
                                        <div className="text-[10px] text-zinc-500 mt-1 italic">
                                            Research positioning: {c.research_positioning}
                                        </div>
                                    ) : null}
                                    {c.differentiation_for_client ? (
                                        <div className="mt-1.5 text-[11px] text-emerald-300">
                                            Our angle: {c.differentiation_for_client}
                                        </div>
                                    ) : c.opportunity_for_client ? (
                                        <div className="mt-1.5 text-[11px] text-emerald-300">
                                            Opportunity: {c.opportunity_for_client}
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    )}
                </Card>

                {/* Aggregated SWOT */}
                <Card icon={Shield} title="Aggregated SWOT" testid="bpg-aggregated-swot">
                    <div className="grid grid-cols-2 gap-2">
                        <SwotCol title="Strengths" items={swot.strengths} tone="emerald" />
                        <SwotCol title="Weaknesses" items={swot.weaknesses} tone="red" />
                        <SwotCol title="Opportunities" items={swot.opportunities} tone="sky" />
                        <SwotCol title="Threats" items={swot.threats} tone="amber" />
                    </div>
                </Card>

                {/* Influencer block */}
                <Card icon={Megaphone} title={`Influencer Strategy (source: ${inf.source || "none"})`} testid="bpg-influencer-block">
                    {(!inf.suggestions?.length && !inf.collaboration_ideas?.length) ? (
                        <Empty label="No influencer data yet. Run influencer match or generate Social Strategy." />
                    ) : (
                        <div className="space-y-2">
                            {(inf.suggestions || []).map((s, i) => (
                                <div key={`s${i}`} className="border border-zinc-800 rounded-sm p-2 text-[12px]"
                                     data-testid={`bpg-influencer-suggestion-${i}`}>
                                    <div className="flex items-center gap-2">
                                        <span className="text-zinc-100 font-medium">{s.name}</span>
                                        {s.handle ? <span className="text-zinc-500">{s.handle}</span> : null}
                                        <span className="text-[10px] font-mono uppercase tracking-wider px-1 py-0.5 border border-zinc-700 text-zinc-400 rounded-sm">
                                            {s.platform || "instagram"}
                                        </span>
                                    </div>
                                    {s.rationale ? <div className="text-zinc-400 mt-1">{s.rationale}</div> : null}
                                </div>
                            ))}
                            {(inf.collaboration_ideas || []).map((i, idx) => (
                                <div key={`c${idx}`} className="border border-zinc-800 rounded-sm p-2 text-[12px]"
                                     data-testid={`bpg-collab-idea-${idx}`}>
                                    <div className="text-zinc-100">{i.idea}</div>
                                    {i.rationale ? <div className="text-zinc-500 text-[11px] mt-1">{i.rationale}</div> : null}
                                </div>
                            ))}
                        </div>
                    )}
                </Card>

                {/* Evidence-Grounded Facts */}
                <Card icon={Shield} title={`Evidence-Grounded Facts (${facts.total_approved || 0} approved)`} testid="bpg-evidence-grounded-facts">
                    {!facts.total_approved ? (
                        <Empty label="No approved evidence yet. Approve some rows in the Research Evidence Panel." />
                    ) : (
                        <div className="space-y-1.5 text-[12px]">
                            {Object.entries(facts.by_subject || {}).map(([kind, rows]) => (
                                <div key={kind} data-testid={`bpg-evidence-group-${kind}`}>
                                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1 mt-2">{kind}</div>
                                    <ul className="space-y-0.5">
                                        {rows.slice(0, 6).map((ev, i) => (
                                            <li key={i} className="text-zinc-300 leading-snug">
                                                <span className="font-mono text-[10px] text-zinc-500">{ev.predicate}:</span>{" "}
                                                {ev.value || "—"}
                                                {ev.source_url ? (
                                                    <a href={ev.source_url} target="_blank" rel="noopener noreferrer"
                                                       className="ml-1 text-sky-400/80 hover:text-sky-300 inline-block align-middle">
                                                        <ExternalLink className="w-2.5 h-2.5 inline" />
                                                    </a>
                                                ) : null}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>

                {/* Business Plan Summary — Sprint A.1: re-surface
                    existing BP agent fields (positioning, audience,
                    objectives, KPIs, channels, risks, next-steps,
                    verified-facts, assumptions). */}
                {(gold.business_plan_summary && (
                    gold.business_plan_summary.positioning
                    || gold.business_plan_summary.business_objective
                    || (gold.business_plan_summary.kpis || []).length > 0
                    || (gold.business_plan_summary.channels || []).length > 0
                )) ? (
                    <Card icon={Sparkles} title="Business Plan Summary" testid="bpg-business-plan-summary" wide>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[12px]">
                            {gold.business_plan_summary.positioning ? (
                                <div data-testid="bpg-bps-positioning">
                                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-0.5">Positioning</div>
                                    <div className="text-zinc-200">{gold.business_plan_summary.positioning}</div>
                                </div>
                            ) : null}
                            {gold.business_plan_summary.business_objective ? (
                                <div data-testid="bpg-bps-objective">
                                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-0.5">Business Objective</div>
                                    <div className="text-zinc-200">{gold.business_plan_summary.business_objective}</div>
                                </div>
                            ) : null}
                            {gold.business_plan_summary.revenue_model ? (
                                <div><span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Revenue: </span><span className="text-zinc-300">{gold.business_plan_summary.revenue_model}</span></div>
                            ) : null}
                            {gold.business_plan_summary.pricing_model ? (
                                <div><span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Pricing: </span><span className="text-zinc-300">{gold.business_plan_summary.pricing_model}</span></div>
                            ) : null}
                            {gold.business_plan_summary.sales_approach ? (
                                <div><span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Sales: </span><span className="text-zinc-300">{gold.business_plan_summary.sales_approach}</span></div>
                            ) : null}
                        </div>
                        {(gold.business_plan_summary.kpis || []).length > 0 ? (
                            <div className="mt-2" data-testid="bpg-bps-kpis">
                                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">KPIs</div>
                                <ul className="space-y-0.5 text-[11px] text-zinc-300">
                                    {(gold.business_plan_summary.kpis || []).slice(0, 6).map((k, i) => (
                                        <li key={i}>• {typeof k === "string" ? k : (k.kpi || k.name || JSON.stringify(k))}{k.target ? ` → ${k.target}` : ""}</li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                        {(gold.business_plan_summary.channels || []).length > 0 ? (
                            <div className="mt-2" data-testid="bpg-bps-channels">
                                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">Channels</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {(gold.business_plan_summary.channels || []).slice(0, 8).map((c, i) => (
                                        <span key={i} className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-zinc-700 text-zinc-400 rounded-sm">
                                            {typeof c === "string" ? c : (c.channel || c.name || JSON.stringify(c))}
                                            {c.role ? ` · ${c.role}` : ""}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </Card>
                ) : null}

                {/* Distribution channels */}
                {dist.length > 0 ? (
                    <Card icon={Megaphone} title="Distribution Channels" testid="bpg-distribution-channels" wide>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                            {dist.map((d, i) => (
                                <li key={i} className="border border-zinc-800 rounded-sm p-2 text-[11px]"
                                    data-testid={`bpg-distribution-row-${i}`}>
                                    <div className="text-zinc-100 font-medium">
                                        {d.channel}{" "}
                                        <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">{d.priority}</span>
                                    </div>
                                    <div className="text-zinc-400 mt-0.5">{d.action}</div>
                                </li>
                            ))}
                        </ul>
                    </Card>
                ) : null}
            </div>
        </div>
    );
}

function Card({ icon: Icon, title, children, testid, wide = false }) {
    return (
        <div className={"border border-zinc-800 rounded-sm bg-zinc-950 p-3 " + (wide ? "lg:col-span-2" : "")}
             data-testid={testid}>
            <div className="flex items-center gap-2 mb-2">
                <Icon className="w-3 h-3 text-zinc-500" />
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-300">{title}</div>
            </div>
            {children}
        </div>
    );
}

function Empty({ label }) {
    return (
        <div className="py-3 text-center text-[11px] text-zinc-500 inline-flex w-full items-center justify-center gap-1">
            <AlertCircle className="w-3 h-3" /> {label}
        </div>
    );
}

function SwotCol({ title, items, tone }) {
    const toneClass = {
        emerald: "text-emerald-300",
        red: "text-red-300",
        sky: "text-sky-300",
        amber: "text-amber-300",
    }[tone] || "text-zinc-300";
    return (
        <div data-testid={`bpg-swot-${title.toLowerCase()}`}>
            <div className={"text-[10px] font-mono uppercase tracking-wider mb-1 " + toneClass}>{title}</div>
            {(items || []).length === 0 ? (
                <div className="text-[10px] text-zinc-600">—</div>
            ) : (
                <ul className="space-y-0.5 text-[11px] text-zinc-300">
                    {(items || []).slice(0, 6).map((s, i) => (
                        <li key={i} className="leading-snug">• {s}</li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function SrcChip({ on, label }) {
    return (
        <span className={"text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border rounded-sm " + (
            on ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/5"
               : "border-zinc-700 text-zinc-500 bg-zinc-900/40"
        )} data-testid={`bpg-src-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            {on ? "✓ " : ""}{label}
        </span>
    );
}
